
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { toast } from 'sonner';
import { validateInviteCode } from '@/utils/oauthProviders';

// Import our new components
import LoginForm from '@/components/auth/LoginForm';
import SignupForm from '@/components/auth/SignupForm';
import InviteCodeForm from '@/components/auth/InviteCodeForm';
import InviteRequestForm from '@/components/auth/InviteRequestForm';

const Login = () => {
  // View state management
  const [currentView, setCurrentView] = useState<'login' | 'signup' | 'invite' | 'request'>('login');
  const [inviteCode, setInviteCode] = useState('');
  const [hasValidInvite, setHasValidInvite] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, validateInvite, isAuthenticated } = useUser();
  
  // Extract invite code from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('invite');
    if (code) {
      setInviteCode(code);
      validateInviteCode(code);
    }
  }, [location]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);
  
  const validateInviteCode = async (code: string) => {
    try {
      const isValid = await validateInvite(code);
      if (isValid) {
        setInviteCode(code);
        setHasValidInvite(true);
        setCurrentView('signup');
        toast.success("Invite code is valid! Please create your account.");
        return true;
      } else {
        toast.error("This invite code is invalid or has expired.");
        return false;
      }
    } catch (error) {
      console.error('Error validating invite:', error);
      toast.error("Unable to validate invite code.");
      return false;
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-hormony-cream/30 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white rounded-2xl shadow-sm p-8 border border-hormony-beige/50">
        {currentView === 'login' && (
          <LoginForm 
            onLogin={(userData) => login(userData)}
            onSwitchToSignUp={() => setCurrentView('invite')}
            onSwitchToRequestInvite={() => setCurrentView('request')}
          />
        )}
        
        {currentView === 'signup' && hasValidInvite && (
          <SignupForm 
            onSignup={(userData, inviteCode) => login(userData, inviteCode)}
            onSwitchToLogin={() => setCurrentView('login')}
            inviteCode={inviteCode}
          />
        )}
        
        {currentView === 'invite' && !hasValidInvite && (
          <InviteCodeForm 
            onValidateInvite={validateInviteCode}
            onSwitchToLogin={() => setCurrentView('login')}
            onSwitchToRequestInvite={() => setCurrentView('request')}
            onValidInvite={(code) => {
              setInviteCode(code);
              setHasValidInvite(true);
              setCurrentView('signup');
            }}
          />
        )}
        
        {currentView === 'request' && (
          <InviteRequestForm
            onSwitchToLogin={() => setCurrentView('login')}
          />
        )}
      </div>
    </div>
  );
};

export default Login;
